<template>
  <div class="ag-grid-single-select-filter">
    <el-input
      v-if="options.length"
      ref="search"
      v-model="keyword"
      class="single-select-search"
      size="small"
      icon="search"
      :placeholder="placeholder" />
    <el-radio-group v-if="visibleOptions.length" v-model="picked" class="single-select-options">
      <el-radio v-for="option in visibleOptions" :key="option.value" :label="option.value" class="single-select-option">
        {{ option.label }}
      </el-radio>
    </el-radio-group>
    <div v-else class="single-select-empty">
      {{ options.length ? noMatchText : emptyText }}
    </div>
    <div class="single-select-actions">
      <el-button size="small" :disabled="!selected" @click="clearSelection">Clear</el-button>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'

/**
 * Column filter for a column the list is read one value of at a time - the bank a payment run is
 * for, where two banks' payments are not printed in one go. A set filter would let the user tick
 * several, so this one offers the same values as radio buttons: picking one is picking it instead
 * of what was picked before.
 *
 * Its model is the shape a set filter's is, a list of the one value asked for, so a query built
 * from the filters reads it the same way either filter is behind the column.
 *
 * Configured through the column's filterParams:
 *   values          The values offered, or a function returning them. Read each time the filter is
 *                   opened, so values still being fetched are picked up.
 *   valueFormatter  Optional, { value } -> label, for values that are not what the user should read
 *   placeholder     Placeholder for the box the list is searched with
 *   emptyText       Shown while there are no values to offer
 *   noMatchText     Shown when the typed text rules out every value on offer
 *   serverFiltered  Whether the query carries this column's criteria, in which case the rows the API
 *                   returned are all kept rather than filtered a second time here
 */
export default defineComponent({
  name: 'AgGridSingleSelectFilter',

  data () {
    return {
      // The value the column is filtered by, which is what the query is built from
      selected: null,
      options: [],
      // Narrows what the popup lists, so a long list is picked from by typing rather than scrolling
      keyword: '',
      // Handed over by the grid when the popup goes up, and how it is put away again
      hidePopup: null
    }
  },

  computed: {
    placeholder () {
      return this.params.placeholder || 'Search'
    },
    emptyText () {
      return this.params.emptyText || 'No values to filter by'
    },
    noMatchText () {
      return this.params.noMatchText || 'Nothing matches that'
    },
    /**
     * The values the popup is listing: the ones on offer, less anything the typed text rules out.
     * Matching on what the user reads rather than on the value behind it.
     * @return {Array}
     */
    visibleOptions () {
      const keyword = this.keyword.trim().toLowerCase()
      if (!keyword) return this.options
      return this.options.filter(option => String(option.label).toLowerCase().includes(keyword))
    },
    /**
     * What the radio buttons are bound to. Picking one is the whole of this filter, so it is applied
     * as it is picked rather than waiting for a button.
     */
    picked: {
      get () {
        return this.selected
      },
      set (value) {
        this.apply(value)
      }
    }
  },

  methods: {
    /**
     * The values on offer, read from filterParams each time the popup opens so a list still being
     * fetched - the banks are loaded in their own right - is picked up rather than left empty
     */
    readOptions () {
      const values = typeof this.params.values === 'function' ? this.params.values() : this.params.values
      const formatter = this.params.valueFormatter
      this.options = (values || [])
        .filter(value => value !== null && value !== undefined && value !== '')
        .map(value => ({ value, label: formatter ? formatter({ value }) : value }))
    },
    /**
     * Filters the column by the one value picked and puts the popup away - there is nothing else to
     * pick here, so the user is done with it
     * @param value
     */
    apply (value) {
      if (value === this.selected) return this.closePopup()
      this.selected = value
      this.filterChanged()
      this.closePopup()
    },
    clearSelection () {
      if (this.selected === null) return
      this.selected = null
      this.filterChanged()
      this.closePopup()
    },
    /**
     * Puts the filter popup away. This filter is shown from the floating filter, which opens the
     * column menu on the filter tab alone, and a menu of one tab has already shown it by the time
     * the popup exists to be closed - so what reaches afterGuiAttached can be nothing at all.
     */
    closePopup () {
      if (this.hidePopup) {
        this.hidePopup()
        this.hidePopup = null
        return
      }
      if (this.params.api) this.params.api.hidePopupMenu()
    },
    filterChanged () {
      this.params.filterChangedCallback()
    },

    /* ag-grid IFilterComp */

    isFilterActive () {
      return this.selected !== null
    },
    /**
     * Where the query is built from what was picked here, the API has already applied it to the rows
     * the grid was given and all of them stay. On the views that still filter client side the column
     * shows the value that was picked.
     */
    doesFilterPass (params) {
      if (this.params.serverFiltered) return true
      if (this.selected === null) return true
      return this.params.valueGetter(params.node) === this.selected
    },
    /**
     * @return {Object|null} The value asked for, as a list the way a set filter holds it, plus the
     *                       name for what the floating filter displays
     */
    getModel () {
      if (this.selected === null) return null
      const option = this.options.find(o => o.value === this.selected)
      return {
        filterType: 'set',
        values: [this.selected],
        names: [option ? option.label : this.selected]
      }
    },
    /**
     * Takes the model a set filter would hold. Several values is the column asking for more than it
     * offers, so the first is what it is read as.
     * @param model
     */
    setModel (model) {
      const values = model && Array.isArray(model.values) ? model.values : []
      this.selected = values.length ? values[0] : null
    },
    getModelAsString () {
      const option = this.options.find(o => o.value === this.selected)
      return option ? option.label : (this.selected || '')
    },
    afterGuiAttached (params) {
      if (params && params.hidePopup) this.hidePopup = params.hidePopup
      this.readOptions()
      // Each visit to the popup starts on the whole list, with the box ready to be typed into
      this.keyword = ''
      this.$nextTick(() => {
        const input = this.$refs.search && this.$refs.search.$el.querySelector('input')
        if (input) input.focus()
      })
    }
  },

  created () {
    this.readOptions()
  }
})
</script>

<style scoped>
  .ag-grid-single-select-filter {
    padding: 8px;
    width: 220px;
  }
  .single-select-search {
    width: 100%;
    margin-bottom: 8px;
  }
  .single-select-options {
    display: block;
    max-height: 220px;
    overflow-y: auto;
  }
  .single-select-options .single-select-option {
    display: block;
    margin: 0 0 6px 0;
    white-space: normal;
  }
  .single-select-empty {
    color: #a7a7a7;
    font-size: 12px;
  }
  .single-select-actions {
    margin-top: 8px;
    text-align: right;
    border-top: 1px solid #e2e2e2;
    padding-top: 8px;
  }
</style>
